import { Grid } from "@mui/material";
import type { GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { useMemo, useState } from "react";
import { saveCsvFile } from "@entrilia/shared/services/downloadFile";
import type { DocumentInfo } from "../../../../../api/types/documentCollectionTypes";
import { createSearchFilter } from "../../searchFilter";
import { documentsToCsv } from "../csv-formatters";
import DocumentsTableInvestorLevel from "./DocumentsTableInvestorLevel";
import type { DocumentsFilterValue } from "./DocumentsViewActions";
import DocumentsViewActions from "./DocumentsViewActions";
import DocumentPreviewContainer from "./document-preview/DocumentPreviewContainer";

interface Props {
  documents: DocumentInfo[];
  showPublishingStatus: boolean;
  showPortalPublishStatus: boolean;
  investorPortalEnabled: boolean;
  showFundName: boolean;
}

const applySearch = createSearchFilter<DocumentInfo>(3, (c) => [c.name, c.investorTitle, c.category]);

const applyFilter = (documents: DocumentInfo[], filterValue: DocumentsFilterValue) => {
  if (filterValue === "Viewed") {
    return documents.filter((d) => d.isViewed);
  }
  if (filterValue === "NotViewed") {
    return documents.filter((d) => !d.isViewed);
  }
  return documents;
};

const getFilterValueCounts = (documents: DocumentInfo[]) => {
  let allCount = 0;
  let viewedCount = 0;
  let notViewedCount = 0;

  for (const doc of documents) {
    if (doc.isViewed) {
      viewedCount++;
    } else {
      notViewedCount++;
    }
    allCount++;
  }

  return { allCount, viewedCount, notViewedCount };
};

const DocumentsViewInvestorLevel = ({
  documents,
  showPublishingStatus,
  showPortalPublishStatus,
  investorPortalEnabled,
  showFundName,
}: Props) => {
  const [filteredItems, setFilteredItems] = useState<DocumentInfo[]>(documents);
  const [filterValue, setFilterValue] = useState<DocumentsFilterValue>("All");
  const [currentSearchTerm, setCurrentSearchTerm] = useState("");
  const [selectedDocuments, setSelectedDocuments] = useState<DocumentInfo[]>([]);

  const { allCount, viewedCount, notViewedCount } = useMemo(() => getFilterValueCounts(documents), [documents]);

  const updateFilteredItems = (documents: DocumentInfo[], searchTerm: string, filterValue: DocumentsFilterValue) => {
    const result = applyFilter(applySearch(documents, searchTerm), filterValue);
    setFilteredItems(result);
  };

  const handleSearch = (searchTerm: string) => {
    updateFilteredItems(documents, searchTerm, filterValue);
    setCurrentSearchTerm(searchTerm);
  };

  const handleFilterChange = (newValue: DocumentsFilterValue) => {
    updateFilteredItems(documents, currentSearchTerm, newValue);
    setFilterValue(newValue);
  };

  const handleExportToCsv = () => {
    const csv = documentsToCsv(filteredItems, "Investor", investorPortalEnabled);
    saveCsvFile(csv, "document_views.csv");
  };

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setSelectedDocuments(documents.filter((doc) => newSelection.ids.has(doc.id)));
  };

  return (
    <Grid container sx={{ height: "100%" }}>
      <Grid size={7} sx={{ height: "100%", pb: 8 }}>
        <DocumentsViewActions
          allCount={allCount}
          viewedCount={viewedCount}
          notViewedCount={notViewedCount}
          filterValue={filterValue}
          showViewFilter={investorPortalEnabled}
          onFilterChange={handleFilterChange}
          onSearch={handleSearch}
          onExportToCsv={handleExportToCsv}
          exportDiabled={filteredItems.length === 0}
        />
        <DocumentsTableInvestorLevel
          documents={filteredItems}
          showPublishingStatus={showPublishingStatus}
          showPortalPublishStatus={showPortalPublishStatus}
          investorPortalEnabled={investorPortalEnabled}
          showFundName={showFundName}
          onSelectionChange={handleSelectionChange}
        />
      </Grid>
      <Grid size={5} sx={{ height: "100%" }}>
        <DocumentPreviewContainer messageLevel="Investor" documents={selectedDocuments} />
      </Grid>
    </Grid>
  );
};

export default DocumentsViewInvestorLevel;
